import { Container, Group, Stack } from '@mantine/core';
import type { RowData } from '@tanstack/react-table';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { Toolbar } from '@apple/ui/data-table/controls/Toolbar/toolbar';
import { TableFilters } from '@apple/ui/data-table/controls/table-filters';
import type { DataTableInstance } from '@apple/ui/data-table/features';
import { RightSidebarPortal } from '@apple/ui/shell/components/RightSidebarPortal';
import { ToggleRightSidebarButton } from '@apple/ui/shell/components/ToggleRightSidebarButton';
import { useRightSidebarCollapsed } from '@apple/ui/shell/contexts/right-sidebar';

type Props<T extends RowData> = PropsWithChildren<{
	title: string;
	table: DataTableInstance<T>;
	toolbarButtons?: React.ReactNode[];
	debug?: boolean;
	/** @default 100vh */
	containerHeight?: React.CSSProperties['height'];
	stackJustify?: React.CSSProperties['justifyContent'];
	hideSidebarToggle?: boolean;
}>;

export function TableLayout<T extends RowData>({
	title,
	table,
	debug = false,
	toolbarButtons = [],
	containerHeight = '100vh',
	stackJustify = 'space-between',
	hideSidebarToggle = false,
	children,
}: Props<T>) {
	const { t } = useTranslation();
	const hasFilterableColumns = useMemo(
		() => table.getAllColumns().some(column => column.getCanFilter()),
		[table],
	);

	const rightSidebar = useRightSidebarCollapsed();

	return (
		<>
			{hasFilterableColumns && (
				<RightSidebarPortal>
					<TableFilters
						table={table}
						clearLabel={t('controls:dataTable.resetFilters')}
						debug={debug}
					/>
				</RightSidebarPortal>
			)}
			<Container p={0} fluid h={containerHeight}>
				<Group gap={0} align='flex-start' wrap='nowrap' w='100%' h='100%'>
					<Stack
						align='stretch'
						justify={stackJustify}
						h='100%'
						w='100%'
						gap={0}
						style={{
							flex: 1,
							direction: table.options.columnResizeDirection,
						}}
					>
						<Toolbar
							groupProps={{ bg: 'var(--apple-header-bg-color)' }}
							title={title}
							rightSection={() => [
								...toolbarButtons,
								hasFilterableColumns &&
									!hideSidebarToggle &&
									(rightSidebar.isCollapsed ||
										(rightSidebar.isSmallerThanBreakpoint &&
											!rightSidebar.isCollapsed)) && (
										<ToggleRightSidebarButton key='right-sidebar-toggle' />
									),
							]}
						/>
						{children}
					</Stack>
				</Group>
			</Container>
		</>
	);
}
